import { useSyncExternalStore } from "react";
import { Code, ConnectError, createClient, type Interceptor } from "@connectrpc/connect";
import { createGrpcWebTransport } from "@connectrpc/connect-web";
import { DDoSEventState, PanelControl, type DDoSEvent, type TrafficSnapshot } from "@/gen/panel_pb";
import { getConfig } from "@/config";
import { getAccessToken, refreshTokens } from "@/api/client";
import { readTokens } from "@/api/tokens";
import { AttackModel, TrafficModel, type Attack, type EventIn, type SnapshotIn, type StreamStatus } from "./model";

const ATTACKS_KEY = "omf.attacks";
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 30_000;

export function toSnapshotIn(s: TrafficSnapshot): SnapshotIn {
  return {
    timestampMs: Number(s.timestampUnixMs),
    filters: s.filters.map((f) => ({
      nodeId: f.nodeId,
      nodeName: f.nodeName,
      filterName: f.filterName,
      filterKernelIndex: f.filterKernelIndex,
      pps: f.pps,
      bps: f.bps,
      droppedRatelimit: Number(f.droppedRatelimit),
      droppedBandwidth: Number(f.droppedBandwidth),
      newSources: Number(f.newSourcesCount),
    })),
  };
}

export function toEventIn(e: DDoSEvent): EventIn | null {
  if (e.state === DDoSEventState.DDOS_EVENT_STATE_UNSPECIFIED) return null;
  return {
    attackId: e.attackId,
    start: e.state === DDoSEventState.DDOS_EVENT_STATE_START,
    nodeId: e.nodeId,
    nodeName: e.nodeName,
    filterName: e.filterName,
    filterKernelIndex: e.filterKernelIndex,
    observedPps: e.observedPps,
    baselinePps: e.baselinePps,
    topSourceIps: [...e.topSourceIps],
    timestampMs: Number(e.timestampUnixMs),
  };
}

function loadAttacks(): Attack[] {
  try {
    const raw = localStorage.getItem(ATTACKS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as Attack[]).filter((a) => a && typeof a.id === "string") : [];
  } catch {
    return [];
  }
}

function saveAttacks(attacks: Attack[]): void {
  try {
    localStorage.setItem(ATTACKS_KEY, JSON.stringify(attacks));
  } catch {
    // history just won't survive a reload
  }
}

const authInterceptor: Interceptor = (next) => async (req) => {
  const token = await getAccessToken();
  if (token) req.header.set("authorization", `Bearer ${token}`);
  return next(req);
};

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(t);
      resolve();
    });
  });
}

export interface StreamInfo {
  status: StreamStatus;
  lastError?: string;
  /** When the next reconnect attempt happens, if status is "reconnecting". */
  retryAtMs?: number;
}

/**
 * Session-lifetime owner of both panel streams. Lives outside React so the
 * streams (and the history they build) survive page navigation; components
 * read it through useLive().
 */
export class LiveStore {
  traffic: TrafficModel;
  attacks: AttackModel;
  trafficStream: StreamInfo = { status: "idle" };
  eventStream: StreamInfo = { status: "idle" };
  version = 0;

  private abort: AbortController | null = null;
  private listeners = new Set<() => void>();
  private notifyScheduled = false;

  constructor(windowSec: number) {
    this.traffic = new TrafficModel(windowSec);
    this.attacks = new AttackModel(loadAttacks());
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getVersion = (): number => this.version;

  /** Coalesces bursts of updates into one render per animation frame. */
  private notify(): void {
    if (this.notifyScheduled) return;
    this.notifyScheduled = true;
    const flush = () => {
      this.notifyScheduled = false;
      this.version++;
      this.listeners.forEach((l) => l());
    };
    if (typeof requestAnimationFrame === "function" && typeof document !== "undefined" && !document.hidden) {
      requestAnimationFrame(flush);
    } else {
      setTimeout(flush, 0);
    }
  }

  ingestSnapshot(s: SnapshotIn): void {
    this.traffic.ingest(s, Date.now());
    this.notify();
  }

  ingestEvent(e: EventIn): void {
    this.attacks.ingest(e);
    saveAttacks(this.attacks.attacks);
    this.notify();
  }

  dismissAttack(id: string): void {
    this.attacks.dismiss(id);
    saveAttacks(this.attacks.attacks);
    this.notify();
  }

  clearEndedAttacks(): void {
    this.attacks.clearEnded();
    saveAttacks(this.attacks.attacks);
    this.notify();
  }

  start(): void {
    if (this.abort) return;
    this.abort = new AbortController();
    this.trafficStream = { status: "idle" };
    this.eventStream = { status: "idle" };
    const signal = this.abort.signal;
    const cfg = getConfig();
    const transport = createGrpcWebTransport({
      baseUrl: cfg.panelBaseUrl || window.location.origin,
      interceptors: [authInterceptor],
    });
    const client = createClient(PanelControl, transport);

    void this.run(
      "trafficStream",
      signal,
      async () => {
        for await (const snap of client.subscribeTraffic({}, { signal, onHeader: this.onOpen("trafficStream") })) {
          this.setStream("trafficStream", { status: "live" });
          this.ingestSnapshot(toSnapshotIn(snap));
        }
      },
    );
    void this.run(
      "eventStream",
      signal,
      async () => {
        // No message may arrive for hours; the stream is "live" once its
        // response headers arrive, not at the first event.
        for await (const ev of client.subscribeDDoSEvents({}, { signal, onHeader: this.onOpen("eventStream") })) {
          const e = toEventIn(ev);
          if (e) this.ingestEvent(e);
        }
      },
    );
  }

  stop(): void {
    this.abort?.abort();
    this.abort = null;
    this.setStream("trafficStream", { status: "stopped" });
    this.setStream("eventStream", { status: "stopped" });
  }

  /** Forget session data (on logout). */
  reset(): void {
    this.stop();
    this.traffic = new TrafficModel(this.traffic.windowSec);
    this.attacks = new AttackModel();
    try {
      localStorage.removeItem(ATTACKS_KEY);
    } catch {
      // ignore
    }
    this.trafficStream = { status: "idle" };
    this.eventStream = { status: "idle" };
    this.notify();
  }

  private onOpen(which: "trafficStream" | "eventStream") {
    return (headers: Headers) => {
      const st = headers.get("grpc-status");
      if (st === null || st === "0") this.setStream(which, { status: "live" });
    };
  }

  private setStream(which: "trafficStream" | "eventStream", info: StreamInfo): void {
    const cur = this[which];
    if (cur.status === info.status && cur.lastError === info.lastError && cur.retryAtMs === info.retryAtMs) return;
    this[which] = info;
    this.notify();
  }

  private async run(which: "trafficStream" | "eventStream", signal: AbortSignal, open: () => Promise<void>) {
    let backoff = BACKOFF_MIN_MS;
    while (!signal.aborted) {
      this.setStream(which, { status: this[which].status === "idle" ? "connecting" : "reconnecting" });
      let lastError: string | undefined;
      try {
        await open();
        // Clean end (server shutting down / proxy timeout): reconnect soon.
        backoff = BACKOFF_MIN_MS;
      } catch (err) {
        if (signal.aborted) return;
        const ce = ConnectError.from(err);
        if (ce.code === Code.PermissionDenied) {
          this.setStream(which, { status: "forbidden", lastError: ce.rawMessage });
          return;
        }
        if (ce.code === Code.Unauthenticated) {
          const held = readTokens();
          const fresh = held ? await refreshTokens(held).catch(() => null) : null;
          if (!fresh) {
            this.setStream(which, { status: "stopped", lastError: "Phiên đăng nhập đã hết hạn" });
            return;
          }
        }
        lastError = ce.rawMessage || ce.message;
        if (this[which].status === "live") backoff = BACKOFF_MIN_MS;
      }
      if (signal.aborted) return;
      const wait = Math.min(backoff, BACKOFF_MAX_MS) * (0.75 + Math.random() * 0.5);
      this.setStream(which, { status: "reconnecting", lastError, retryAtMs: Date.now() + wait });
      await sleep(wait, signal);
      backoff = Math.min(backoff * 2, BACKOFF_MAX_MS);
    }
  }
}

let store: LiveStore | null = null;

export function getLiveStore(): LiveStore {
  if (!store) {
    store = new LiveStore(getConfig().trafficWindowSec);
    // Test/debug hook (dev builds only): lets e2e tests read stream state.
    if (import.meta.env.DEV) (window as unknown as { __omfLive?: LiveStore }).__omfLive = store;
  }
  return store;
}

/** Re-renders the caller whenever live data or stream status changes. */
export function useLive(): LiveStore {
  const s = getLiveStore();
  useSyncExternalStore(s.subscribe, s.getVersion);
  return s;
}
